/**
 * "Continue with Apple" button — works on both the web PWA (via Apple's
 * AppleID JS SDK loaded in index.html) and inside the native iOS Capacitor
 * shell (via @capacitor-community/apple-sign-in plugin, dynamically loaded).
 *
 * On success we POST the identity_token to /api/auth/apple — backend verifies
 * the JWT against Apple's JWKS, finds-or-creates the user, and returns our
 * own session JWT identical to the email/password + Google flows.
 *
 * Required env vars (frontend):
 *   REACT_APP_APPLE_CLIENT_ID  — your Apple Service ID (e.g. "app.bosunsmate.signin")
 *   REACT_APP_APPLE_REDIRECT_URI — exactly matches the Return URL configured
 *                                  in the Apple Developer Portal Service ID
 *                                  (e.g. "https://bosunsmate.app/auth/apple/callback")
 */
import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { isNativeApp } from "@/lib/platform";
import { trackSignup } from "@/lib/analytics";

const WEB_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || "";
const WEB_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI || "";

function ensureAppleJSInit() {
    if (typeof window === "undefined") return false;
    if (!window.AppleID) return false;
    if (!WEB_CLIENT_ID || !WEB_REDIRECT_URI) return false;
    if (window.__bm_apple_initialised) return true;
    try {
        window.AppleID.auth.init({
            clientId: WEB_CLIENT_ID,
            scope: "name email",
            redirectURI: WEB_REDIRECT_URI,
            usePopup: true,
            state: "bosunsmate_login",
        });
        window.__bm_apple_initialised = true;
        return true;
    } catch (e) {
        console.warn("[apple] AppleID.auth.init failed", e);
        return false;
    }
}

async function signInWeb() {
    if (!ensureAppleJSInit()) {
        throw new Error(
            "Sign in with Apple is not configured for this build. " +
            "(Set REACT_APP_APPLE_CLIENT_ID + REACT_APP_APPLE_REDIRECT_URI)"
        );
    }
    const res = await window.AppleID.auth.signIn();
    const idToken = res?.authorization?.id_token;
    if (!idToken) throw new Error("Apple did not return an identity_token");

    // Apple only returns name on the FIRST consent. Pass it through.
    const u = res?.user || {};
    const firstName = u?.name?.firstName || "";
    const lastName = u?.name?.lastName || "";
    const name = `${firstName} ${lastName}`.trim() || undefined;

    return { identity_token: idToken, name, platform: "web" };
}

async function signInNative() {
    // Lazy-load to avoid bundling a Capacitor-only plugin into the web build
    const mod = await import("@capacitor-community/apple-sign-in").catch(() => null);
    if (!mod || !mod.SignInWithApple) {
        throw new Error("Apple Sign-In plugin not installed in this native build.");
    }
    const result = await mod.SignInWithApple.authorize({
        clientId: WEB_CLIENT_ID || "app.bosunsmate",
        redirectURI: WEB_REDIRECT_URI || "https://bosunsmate.app/auth/apple/callback",
        scopes: "email name",
        state: "bosunsmate_login_ios",
    });
    const idToken = result?.response?.identityToken;
    if (!idToken) throw new Error("Apple Sign-In did not return identityToken");

    const givenName = result?.response?.givenName || "";
    const familyName = result?.response?.familyName || "";
    const name = `${givenName} ${familyName}`.trim() || undefined;

    return { identity_token: idToken, name, platform: "ios" };
}

/**
 * Trigger Apple Sign-In and resolve to the raw payload the backend expects
 * (`{identity_token, name?, platform}`). Used both by the login button (POSTs
 * to /auth/apple) and the Account Settings link flow (POSTs to /auth/apple/link).
 */
export async function getAppleIdentityPayload() {
    return isNativeApp() ? await signInNative() : await signInWeb();
}

export default function AppleSignInButton({ onAuthed, nextPath, label = "Continue with Apple" }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const onClick = async () => {
        setErr("");
        setBusy(true);
        try {
            const payload = await getAppleIdentityPayload();
            const { data } = await api.post("/auth/apple", payload);
            // First-time Apple sign-up → fire Google Ads conversion once.
            // Backend flips is_new_user off on returning login.
            if (data.is_new_user) trackSignup({ transactionId: data.id });
            if (onAuthed) onAuthed(data, nextPath);
        } catch (e) {
            // Apple JS SDK throws { error: "popup_closed_by_user" } when the user
            // closes the popup — don't surface that as a noisy red error.
            const code = e?.error || e?.code;
            if (code === "popup_closed_by_user" || code === "user_cancelled_authorize") {
                setBusy(false);
                return;
            }
            const detail = e?.response?.data?.detail;
            setErr(formatApiErrorDetail(detail) || e?.message || "Apple Sign-In failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <button
                data-testid="apple-signin-btn"
                type="button"
                className="bm-btn bm-btn-ghost"
                style={{ width: "100%", justifyContent: "center", padding: "12px 16px", display: "flex", alignItems: "center", gap: 8 }}
                onClick={onClick}
                disabled={busy}
                aria-label={label}
            >
                <AppleLogo />
                <span>{busy ? "Connecting to Apple…" : label}</span>
            </button>
            {err && (
                <div data-testid="apple-signin-error" style={{ marginTop: 8, color: "var(--bm-bad)", fontSize: 12 }}>
                    {err}
                </div>
            )}
        </div>
    );
}

function AppleLogo() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
            <path d="M11.182.008c.085 1.117-.302 2.04-.94 2.733-.611.673-1.529 1.211-2.456 1.13C7.69 2.85 8.16 1.917 8.74 1.244 9.36.519 10.434.057 11.182.008zM14.32 12.057a4.55 4.55 0 0 1-.706 1.282c-.46.69-1.108 1.55-1.91 1.557-.713.006-.897-.466-1.866-.456-.97.01-1.17.467-1.886.461-.802-.007-1.418-.78-1.879-1.466-1.286-1.911-2.097-5.158-.876-7.349.59-1.062 1.69-1.748 2.847-1.765.78-.014 1.519.526 1.999.526.477 0 1.376-.65 2.319-.554.395.017 1.504.16 2.216 1.207-.06.04-1.323.774-1.309 2.31.017 1.836 1.612 2.446 1.622 2.451z" />
        </svg>
    );
}
